import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Add, Check, Close, Delete, Edit } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { WebhookConfig, useDeleteWebhook, useWebhooks } from '@frontend/features/webhooks/queries';
import { WebhookModal } from '@frontend/features/webhooks/components/WebhookModal';

export const WebhooksList = () => {
  const { id } = useParams();
  const agencyId = Number(id);
  const { t } = useTranslation();
  const pagination = usePagination();
  const { addToast } = useSnackBar();

  const { data, isLoading, refetch } = useWebhooks(agencyId);
  const deleteMutation = useDeleteWebhook(agencyId);

  const { data: workflowsData } = useGetWorkflowsByAgency({
    agencyId,
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const workflowNameById = useMemo(
    () =>
      (workflowsData?.items || []).reduce<Record<string, string>>((acc, wf) => {
        acc[wf.id] = wf.name;
        return acc;
      }, {}),
    [workflowsData],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookConfig | undefined>(undefined);

  const handleModalOpen = (webhook?: WebhookConfig) => () => {
    setSelectedWebhook(webhook);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setSelectedWebhook(undefined);
    setIsModalOpen(false);
    refetch();
  };

  const handleDelete = (webhook: WebhookConfig) => async () => {
    const confirmed = window.confirm(t('webhooks.confirmDelete'));
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(webhook.id);
      addToast({ message: t('webhooks.deleteSuccess'), color: 'success' });
      refetch();
    } catch (e) {
      addToast({ message: t('webhooks.deleteError'), color: 'error' });
    }
  };

  const renderWorkflows = (workflowIds: string[] | null) => {
    if (!workflowIds || workflowIds.length === 0) {
      return t('webhooks.allWorkflows');
    }
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {workflowIds.map((wfId) => (
          <Chip key={wfId} label={workflowNameById[wfId] || wfId} size="small" />
        ))}
      </Box>
    );
  };

  const renderEvents = (events: string[]) => (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {events.map((event) => (
        <Chip key={event} label={t(`webhooks.events.${event}`, { defaultValue: event })} size="small" />
      ))}
    </Box>
  );

  const count = data?.length || 0;

  return (
    <ListLayout padding={0} title={t('webhooks.title')} subtitle={t('webhooks.subtitle')}>
      <Box
        display="flex"
        alignItems="center"
        gap={(theme) => theme.spacing(3)}
        marginBottom={(theme) => theme.spacing(3)}
      >
        <Typography variant="subtitle1" align="left" sx={{ flexGrow: 1 }}>
          {count} {t('webhooks.results')}
        </Typography>

        <SearchInput placeholder={t('webhooks.searchPlaceholder')} onSearch={() => {}} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: (theme) => theme.spacing(5),
            '& button': {
              padding: (theme) => theme.spacing(1),
              width: (theme) => theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={handleModalOpen()} value="add" aria-label="add webhook">
            <Add />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isModalOpen && (
        <WebhookModal open={isModalOpen} onClose={handleClose} agencyId={agencyId} webhook={selectedWebhook} />
      )}

      {count > 0 && (
        <Table
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          columns={[
            { id: 'actions', label: t('webhooks.table.actions'), align: 'left' },
            { id: 'url', label: t('webhooks.table.url'), align: 'left' },
            { id: 'events', label: t('webhooks.table.events'), align: 'left' },
            { id: 'workflows', label: t('webhooks.table.workflows'), align: 'left' },
            { id: 'active', label: t('webhooks.table.active'), align: 'left' },
          ]}
          data={{
            key: 'id',
            items: (data || []).map((item) => ({
              ...item,
              url: <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{item.url}</Typography>,
              events: renderEvents(item.events),
              workflows: renderWorkflows(item.workflowIds),
              active: item.isActive ? <Check color="success" /> : <Close color="error" />,
              actions: (
                <>
                  <IconButton onClick={handleModalOpen(item)}>
                    <Edit color="primary" />
                  </IconButton>
                  <IconButton onClick={handleDelete(item)}>
                    <Delete color="error" />
                  </IconButton>
                </>
              ),
            })),
            totalCount: count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}

      {count === 0 && !isLoading && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography>{t('webhooks.empty')}</Typography>
          <Button variant="contained" onClick={handleModalOpen()}>
            {t('webhooks.createCta')}
          </Button>
        </Box>
      )}
    </ListLayout>
  );
};

export default WebhooksList;
